import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FaHome, FaHeartbeat, FaUserMd, FaHandHoldingHeart, FaCheckCircle, FaClock, FaUserNurse, FaFlask } from 'react-icons/fa';
import useScrollToHash from '../hooks/useScrollToHash';
import './CoreServices.css';
import homeBasedImage from '../images/services/homebased.jpeg';
import elderlyImage from '../images/services/eldercare.jpeg';
import palliativeImage from '../images/services/pallative.jpeg';
import doctorVisitImage from '../images/services/Doctor-Visit.png';
import elderly1Image from '../images/services/elderly1.png';
import labImage from '../images/services/labtest.jpg';
const CoreServices = () => {
  useScrollToHash(120);
  const coreServices = [
    {
      id: "home-based-care",
      icon: <FaHome />,
      title: "Home Based Care",
      description: "Comprehensive medical and supportive care delivered in the comfort of your home.",
      details: [
        "Skilled nursing care 24/7",
        "Medication management and administration",
        "Vital signs monitoring",
        "Personal hygiene assistance",
        "Nutritional support and meal preparation",
        "Medical equipment setup and training"
      ]
    },
    {
      id: "elderly-care",
      icon: <FaUserNurse />,
      title: "Elderly Care",
      description: "Specialized care for senior citizens ensuring dignity, comfort, and well-being.",
      details: [
        "Daily living assistance (ADL support)",
        "Mobility support and fall prevention",
        "Companionship and emotional support",
        "Memory care for dementia patients",
        "Chronic disease management",
        "Regular health monitoring"
      ]
    },
    {
      id: "physiotherapy-services",
      icon: <FaHandHoldingHeart />,
      title: "Physiotherapy Services",
      description: "Professional rehabilitation therapy to restore mobility and improve quality of life.",
      details: [
        "Customized exercise programs",
        "Post-surgery rehabilitation",
        "Joint and muscle therapy",
        "Sports injury rehabilitation",
        "Neurological rehabilitation",
        "Balance and gait training"
      ]
    },
    {
      id: "lab-services",
      icon: <FaFlask />,
      title: "Laboratory Services",
      description: "Comprehensive diagnostic testing with convenient home sample collection and quick digital reports.",
      details: [
        "Home sample collection by experts",
        "Complete blood work & biochemistry",
        "Preventive health packages",
        "Same-day results for routine tests",
        "Secure digital reports delivery",
        "Regular health monitoring panels"
      ]
    },
    {
      id: "doctor-home-visit-services",
      icon: <FaUserNurse />,
      title: "Doctor Home Visit",
  description: "Expert medical consultation by qualified physicians in the comfort of your home.",
      details: [
        "Professional doctor consultation at home",
        "Comprehensive health assessment",
        "Prescription and medication review",
        "Specialist referrals when needed",
        "Follow-up care coordination",
        "Digital medical records maintenance"
      ]
    },
    {
      id: "palliative-care-services",
      icon: <FaHeartbeat />,
      title: "Palliative Care",
      description: "Compassionate support and symptom management for patients with serious illnesses, focusing on comfort, dignity, and quality of life for both patients and families.",
      details: [
        "Pain and symptom management",
        "Emotional and psychological support",
        "End-of-life care planning",
        "Coordination with specialists and family",
        "Spiritual care and counseling",
        "Support for caregivers and loved ones"
      ]
    }
  ];

  return (
    <div className="core-services-page">
      <section className="page-hero">
        <Container>
          <h1>Core Services</h1>
          <p>Comprehensive Home Healthcare Solutions</p>
        </Container>
      </section>

      {/* Detailed Services Section */}
      <section className="detailed-services">
        <Container>
          <div className="section-intro text-center mb-5">
            <h2>Our Core Healthcare Services</h2>
            <p className="lead">
              At Aroghya Radham, we bring professional medical care to your home. Our core services are designed 
              to provide comprehensive healthcare solutions that prioritize comfort, dignity, and quality of life.
            </p>
          </div>

          {coreServices.map((service, index) => (
            <div key={index} id={service.id} className={`service-detail-row ${index % 2 === 1 ? 'reverse' : ''}`}>
              <Row className="align-items-center mb-5">
                <Col lg={6} className={index % 2 === 1 ? 'order-lg-2' : ''}>
                  <div className="service-detail-content">
                    <div className="service-icon-large">
                      {service.icon}
                    </div>
                    <h3>{service.title}</h3>
                    <p className="service-description">{service.description}</p>
                    <ul className="service-features">
                      {service.details.map((detail, idx) => (
                        <li key={idx}>
                          <FaCheckCircle className="check-icon" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Col>
                <Col lg={6} className={index % 2 === 1 ? 'order-lg-1' : ''}>
                  <div className="service-image-wrapper">
                    <img
  src={
    index === 0 ? homeBasedImage :
    index === 1 ? elderly1Image :
    index === 2 ? elderlyImage :
        index === 3 ? labImage :
        index === 4 ? doctorVisitImage :

    palliativeImage
  }
  alt={service.title}
  className="img-fluid rounded shadow-lg"
/>
                  </div>
                </Col>
              </Row>
            </div>
          ))}
        </Container>
      </section>
      
      {/* Why Choose Our Core Services */}
      <section className="service-benefits">
        <Container>
          <h2 className="text-center mb-5">Why Choose Our Core Services?</h2>
          <Row className="g-4">
            <Col md={3}>
              <Card className="benefit-card text-center h-100">
                <Card.Body>
                  <FaUserNurse className="benefit-icon" />
                  <h4>Professional Care</h4>
                  <p>Certified healthcare professionals with years of experience</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="benefit-card text-center h-100">
                <Card.Body>
                  <FaHandHoldingHeart className="benefit-icon" />
                  <h4>Personalized Plans</h4>
                  <p>Customized care plans tailored to individual needs</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="benefit-card text-center h-100">
                <Card.Body>
                  <FaClock className="benefit-icon" />
                  <h4>24/7 Support</h4>
                  <p>Round-the-clock assistance and emergency response</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="benefit-card text-center h-100">
                <Card.Body>
                  <FaCheckCircle className="benefit-icon" />
                  <h4>Quality Assured</h4>
                  <p>Continuous monitoring and improvement of care standards</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default CoreServices;
